import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const voiceSalesWinbackMachineConfig = {
  id: 'voiceSalesWinbackMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'fuelType',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.voiceSalesWinback
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    fuelType: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        CHANGE_ADDRESS: {
          target: 'home'
        }
      },
      meta: {
        path: routePath.fuelType
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'personaliseQuote'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ]
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    mixedMeters: {
      on: {
        NEXT: {
          target: 'personaliseQuote'
        }
      },
      meta: {
        path: routePath.mixedMeters
      }
    },

    personaliseQuote: {
      on: {
        NEXT: [
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'paymentMethod',
            guard: 'isPrePayOnly'
          },
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.personaliseQuote
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    paymentMethod: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.prepayInfo
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'home'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'tariffDetails'
        },
        CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    tariffDetails: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.tariffDetails
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebitType',
            description:
              "and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])",
            guard: and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'psrEntry'
          }
        ],
        CHANGE_ADDRESS: { target: 'home' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebitType: {
      on: {
        NEXT: {
          target: 'directDebit'
        }
      },
      meta: {
        path: routePath.directDebitType
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'psrEntry'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'psrCapture',
            description: `and(['hasPSR', not('releaseRevampJourney')])`,
            guard: and(['hasPSR', not('releaseRevampJourney')])
          },
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    psrCapture: {
      on: {
        NEXT: {
          target: 'userPreferences'
        }
      },
      meta: {
        path: routePath.psrCapture
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    authResponse: {
      meta: {
        path: routePath.authResponse
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDcD2BLAxmAygQwBs4B1dAOwCM9MBrAWWoAtywA6R1AWzAGIA5AKIANACoBtAAwBdRKAAOqWOgAu6VGVkgAHogDsugCysAnBIDMAVgsAOYwCYJARgNnrAGhABPPWcesLAGyOZroWutZmvnYWAL4xHmhYuIQk5FS0DJjMZGwc3PzC4o4ySCAKSqrqmjoI+kamljb2Ti7uXohBdqy6xsa6ZvaOxtZhunEJGNj4RLCklNT0TCzsXLyComJ2JfKKKmoapTV1JuZWtg7Orh7eCI4BxgH+zroBFgYB92Yu4yCJUymzNILTLZXKrAobMzbMq7SoHUBHQwnRrnFpXdq3AxWVgBWyOHqOazvVxmH5-ZIzObpRZZZZ5XgAIQAMgB5ADCAGkAPoAKRZAFUAEqCACakmh5T2VUOeiRDTOzUubRuwT8zhGH1eFjsHzsdjJkwpqXmGSWOVYFAArkocrBYPy5BA8Mo4ABBMhkVCWsjYbhkZQQ8TSTSSuHVRDOOx+FxfEKWaz464R6L+D59Ax2LG9V4BA1JabG6kg5ZWm1we2O51uj1en1gP0B11M1nEXkC4UCMXB0qh-bhhD2eoZgK6CQBVpvXRJhAJ1hmPXDcy6Ox9CR2ax5-6UoGm2nm0ssO0Op0u2A4S0UTgqAScPDoAiB8Uh2F9mUIC5qyIjYyWOwE6eOCmgT3IYmYWNmgSbkagImjSoKsAAZpaYAECInhyGshRPj2L7SgiEYZtGkQuP0NiJhiwS+KwGaGBICY-n+LxQQWMFFmabBIShaEYY+xTPhUr74ZiUbUcRcZkVOGL9GYrBRtE85mEEEgSGEzEAlSwLsYhyGoehmEbFs-FSvC2gESJMYkfG5EqhIxhGFiEg9H0QRDFianbrBxbmpxuk8WyAASrp8AA4gIXKugAIhFgoCDgODYTsAl4aZA7ON0Bh0Y4ymuPoTjTrifikeExgWE4Ti4u5haaXubDcC6ABOCjkMoABqhDoCe+yPt2iXGf2H6sJR36-v+GIjDJATRFGXzhLY1gbvEvyGixGm7vBdVgI1GD+m1BAdc6XXrEUEq4SZNQDUN4EjcYjgAWEjxZUMy7zmE1iTZVrHVetYANU1O3tZ16iPoZOFJWdiAXV+V3aqNKqRBYskvK4AQSC4BimAYH2rXBywbVtzW7ftcI8MF-IAJIRWFbJMmTnIJTCYP9U4n6uNDf43QBUaPOY+IOBEtiKbEi3kitO44+asAoWAmDKCychwrAPACDTwVk8yYUAGL8sr9O9sl53jsYJh6tYy4vME+iSSqUZGG9lj2KYdhfJmWNi15bCS0QMtywrSsRWTIjhVFMVxbrp39YBMl9NEDtyX0ZjTujXTI3ZKPhKOwyu55Wme9Lsvy-siv+SydBhXQLItQIYeM2+Oro8b65m0EIT6NOgQSN0FiUaBUaowtEz5upbs51L3sF+oRcl2XFdV3xoN9bXhsN6bf7N5b079EbgTquEY6BExwvLUP2c1awudj77xel1y5eV5sJ010JddG-YjerxbrcYgVrCOfitlmKjAwGV9SH0Hh5Nip8rxaEgHQH6m1FZHWrgvJ+S9X4r3Ni3K2EZ7iPC7gYF4bw0yxizhA+CGF6qwHUO1SWABFS0qAXTdQfsglKkQ-ysATPOSaIxCS2SwZicw3RHBWCAWRZwbwSFfWWOQyhZBqFgDoQw-Sx0jJhjfGw3QHDgg6nXF3awfCALvC6A4bUjgsooycP3JaYCqprWkfAqhe1aH0MYYgkGvU1FCQ0VorhujeF9E5hEOca5tQw2GAYYIki7HmjAMgEQeB6roAQghMmZAEKoHqreYmiCeoMxYTUVwDgcS6GcM4fBZj5z8OcIIkpIisQJnEWMUBW5bHizYHIPAngGywOUBwCATDVGCVYdYIpI5SlAJKdoqpRjZIqSjOY5ShIoltNYJgCelpODjzIAIf09VPADPnp4lKGYMo-2Kk5feLxDEjm6G9CwAwHj6OJMs92qz1mbLhDs5QezeLMKOQUkZHcxkRImRUv8AFhiaLuDYHUykRklJeVpGRjjUnpMyQddQ55OC3h+Tkv5QyCn6EGopXErhhFmzCBvFMZjeYDAKvJRFp9kVyIIKijJWT9hYpxfsgKQVQpB2irFeKuS9bgwQC3YlHx+bkr-JSr+oRZJvQtriGFJVGVkIcSytl6K4RcoSfsrWysuQiBFAABSriK8O6iiXBClWSsIsr+GDlkoBe5TslIqSaQPFpn1ontM1YQbVHLMUbO5TwOgZMhACAijfAQIgBCCmFfi-WiAJW2tJQmB1oQqkJkePYF4o4Ai+EiMudV9iKEorSeyjFZA9U-JwMrAQbJA4slNSIMmLI+BJsGSm8VNqSXSqzXKm46Yf5ksJOOSITghbeugtjV5zLA1Vp1Zy0N+qeDmsTZ2psZMG1chofyFk8akH-NTf2u1maKU5o+J3AB843olKdmW80i7WXLuDbWtdPzeUhSpp2nA-I6Bto7XwE9BKz1QoHfaq905e6aNekEG66MInGGfWwAAji45RYHe3vHxIjEq-RMzLnAgBSIfhcQ6n6BEPoWUrEi2PqQ5YmGlFKzoK6MmTJ92HuPZax+xygiaPZmEec+CY6GKopESIwxfCARGTO6xPr51aRY4wtk-7APAc7ThsVLgVwuoASMJOYFDH4YeLZe5KrQhjjQ6wVTvBDVMh0-2IBY5qKvHCPgiITtlRnr8OmYRK4QjbzsrZ5QCSkkIQij9O8BAEFYT4-kxAScjBBBHP0fErQDBkaopRkcJJaN0Vs-WWLijXEJeTbpyjszPMo0zKnMjs5SrhEAmuOaFVmlzuHqfTA9UwBVldJgNZ3oAx4p7WK+5ykTCEmXPomwImAKOUeOOHoThTCjhGF6xTXWT7wV6-1l0g3hv+l+eN-sk2O43RXnNzbCcKJ6i6GcSZDFJoZls-tgbQ3ayjawu4vJp6EAXem9dlSt2csI2emBXo4R7bvb65947AYf38sioK0OiWAcpZxHcfQvhDCuGyxiaIfht7lPolK1DnXRa7eWBAdAfWZbRYoCobi2GMfgcBzwjhqNFJvR1KbACWUujiKuwSfBRbbN04Z8oJnKgDkeI5yjWw3PAggrTP0Tmwi5xZSxLoqV4RbNyFgPVL5uKKtnbfEWuiJhQmAVCFdu71s0r4Oe9qeG3wqeMakS+43pv9mILngr3tVvrA2+J7Kh3OXba2BujqccpUXae-Ad79pxu2R4HlpaPr8v-uK4+CTpGhIVwRDopr+y+gzElSdpYD3s7qdMfNNaTapq+sIU2mAOs8WNjs+Dz+UPcZHJzT6G9Mj7Dxx3DuMRgwDTbMZIgJtOt-vzeHI58uQRQDp-KV6MIh4o-NHj7S1PmfPxPTz-gKUBjye-UW6EgAWgCNOe-P9lIv9f4s0cCnL+tNefSG-KVJoIyZjTR0SKQriOQbyEhjrqh0QqTagjK2YHi2gVgnjViejei+gd7KB-41A0pGy0TPR0RhDBCcwQ5fC9DTo-iWAjgIHWiHjIFVhngXhXjKA3ixbYERj9AdzCJ0R8x6jLhAIkHdBkGmA77wzUFJ7f5aSIHljHgMHniXjXi3j3jnhDbljsHvgAKaJ0rQ56jTq3T3akHowiFXRUFbZf6+orI+Ss7qGAR3DUTgTEixjoy+a1BfCyRBY3Q4KvBDC2Z4x-StQAw1o2G7wcKTr6I5glIj4UTkb+AlRRAgGKT4i+FwL1SmrbTKDBSWgdRgDqFOyORzgPA6EXBdya4PTZjEZgEOC5gSEWGvLnz5wKy5EkgnD9ChB5E+aE43CkQcJPKgFdy2S+HoDQIQA9LwJNHag-x-jowAKCal4UQ4I4gjJEJhCWKf5HxX4rKvroDOJKLqGaGPDzSmCTSWJvRFqGITHWYjjmBgSjiYw1HKanyxLxKJLJJBpBEr69qgH75FpvDow8HvCGKCLzRfDT7lILhmHrGSFMqdLdI-R9J7FFpGChCWAAJUZ5GdEESTRzhEY6jxyWD9DvbvJbJ+7qEZT4IEb4KyaT7ODnFGymxSYx755Pr3HdYaoVparvo1qL6kl2Cwa2TGILiuQRKAQ-i2b2akmlQPS6jBBvSmBriGKjLribZhA2AzRhYRbJLRbhb3jn5B66bhAIwv4jD1bmwuGUR+AjCnC873prE2K1FaQlb3hlY5EfH6kjDP6EEmlBBmkZgIw3TQ5mI9D3A+Esk07mgfaHZfYjbqFdz6Zpa4iTTTTrij6PD870krhmw2ahkN5sBS55yy7KDWGunnbfyZhALBYZRWBVJC7a4DAzR9zCJ3F15e5+qsB5mM5gDM5YHFlvjgRvCxGtGlS9C-Ga4IxebvCmyjhrgIrZkp6sBG4m67I3B6n9iIaPbDArY-gPBYij6h7ji6L1lzRNnbb15zkLnp6Z59bqGvBAQozvC9BALjgjBl44gOROwRKxhvS2ZN6pGt7t6d7XkvBGCKQMQu62QjJ77c5V5ODBY3mz71Tz71Tck9lCS3FqgfCEFGIAnRHsL6LQUWyBDajwWIVshcByBEAujqGcGpkhD6KGD7n3CQV4XagwWhBwWzmtlgBaANQsqkX+jUDKD8i6m56fEqQdyZgjAjKZiRBBAYmA4PCDQfD0XBCEIhDFbcWbQsoMhQBsgEB4B2hYD+SrB0CoDIAukrnqJiWyTT4wrSWJFyWlkDAkZRiWyikcUrJcU8WEAmVmWpJ7FWUSW2UxiyUbwDDdArjsz5Z0oLRxBAA */
      ...voiceSalesWinbackMachineConfig
    },
    {
      guards
    }
  );
